import { addAttachments } from '@blocksuite/affine-block-attachment';
import { addImages } from '@blocksuite/affine-block-image';
import {
  CanvasElementType,
  EdgelessCRUDIdentifier,
} from '@blocksuite/affine-block-surface';
import {
  insertEdgelessTextCommand,
  mountTextElementEditor,
} from '@blocksuite/affine-gfx-text';
import {
  MAX_IMAGE_WIDTH,
  type MindmapStyle,
  TextElementModel,
} from '@blocksuite/affine-model';
import {
  FeatureFlagService,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import { openSingleFileWith } from '@blocksuite/affine-shared/utils';
import { Bound, type IVec } from '@blocksuite/global/gfx';
import type { BlockComponent } from '@blocksuite/std';
import type { TemplateResult } from 'lit';
import * as Y from 'yjs';

export type ConfigProperty = 'x' | 'y' | 'r' | 's' | 'z' | 'o';
export type ConfigState = 'default' | 'active' | 'hover' | 'next';
export type ConfigStyle = Partial<Record<ConfigProperty, number | string>>;
export type ToolConfig = Record<ConfigState, ConfigStyle>;

export type DraggableTool = {
  name: 'text' | 'mindmap' | 'media';
  icon: TemplateResult;
  config: ToolConfig;
  standardWidth?: number;
  render: (bound: Bound, edgeless: BlockComponent) => Promise<string | null>;
};

const unitMap = { x: 'px', y: 'px', r: 'deg', s: '', z: '', o: '' };
export const textConfig: ToolConfig = {
  default: { x: -20, y: -26, r: 7.74, s: 0.92, z: 3 },
  active: { x: -22, y: -30, r: -8, s: 0.92 },
  hover: { x: -22, y: -30, r: -8, s: 1, z: 3 },
  next: { x: -22, y: 64, r: 0 },
};
export const mindmapConfig: ToolConfig = {
  default: { x: 0, y: -8, s: 1, r: 0, z: 2 },
  active: { x: 0, y: -8, r: 0, s: 1 },
  hover: { x: 0, y: -8, r: 0, s: 1, z: 3 },
  next: { y: 64, r: 0 },
};
export const mediaConfig: ToolConfig = {
  default: { x: -20, y: -28, r: 23, s: 1.2, z: 1 },
  active: { x: -25, y: -35, r: -9, s: 1.2 },
  hover: { x: -25, y: -35, r: -9, s: 1.5, z: 3 },
  next: { y: 64, r: 0 },
};

export const getMindmapRender =
  (mindmapStyle: MindmapStyle, layoutType: number = 0, depth: number = 1): DraggableTool['render'] =>
  async (bound, edgeless) => {
    const [x, y, _, h] = bound.toXYWH();

    const rootW = 145;
    const rootH = 50;

    const nodeW = 80;
    const nodeH = 35;

    const centerVertical = y + h / 2;
    const rootX = x;
    const rootY = centerVertical - rootH / 2;

    type MindMapNode = {
      children: MindMapNode[];
      text: string;
      xywh: string;
    };

    const root: MindMapNode = {
      children: [],
      text: 'Mind Map',
      xywh: `[${rootX},${rootY},${rootW},${rootH}]`,
    };

    if (layoutType === 2) {
      // Balanced layout: children on both left and right
      // Left children
      for (let i = 0; i < 2; i++) {
        const nodeX = x - 200 - nodeW;
        const nodeY = centerVertical - nodeH / 2 + (i - 0.5) * 60;
        const node: MindMapNode = {
          children: [],
          text: 'Left Node',
          xywh: `[${nodeX},${nodeY},${nodeW},${nodeH}]`,
        };
        if (depth > 1) {
          for (let j = 0; j < 2; j++) {
            const subX = nodeX - 150 - nodeW;
            const subY = nodeY - 30 + j * 60;
            node.children.push({
              children: [],
              text: 'Sub Left',
              xywh: `[${subX},${subY},${nodeW},${nodeH}]`,
            });
          }
        }
        root.children.push(node);
      }
      // Right children
      for (let i = 0; i < 2; i++) {
        const nodeX = x + rootW + 200;
        const nodeY = centerVertical - nodeH / 2 + (i - 0.5) * 60;
        const node: MindMapNode = {
          children: [],
          text: 'Right Node',
          xywh: `[${nodeX},${nodeY},${nodeW},${nodeH}]`,
        };
        if (depth > 1) {
          for (let j = 0; j < 2; j++) {
            const subX = nodeX + 150 + nodeW;
            const subY = nodeY - 30 + j * 60;
            node.children.push({
              children: [],
              text: 'Sub Right',
              xywh: `[${subX},${subY},${nodeW},${nodeH}]`,
            });
          }
        }
        root.children.push(node);
      }
    } else {
      // Left or Right layout
      const dir = layoutType === 1 ? -1 : 1; // Left is negative direction, Right is positive
      const startX = dir === 1 ? x + rootW + 200 : x - 200 - nodeW;
      
      for (let i = 0; i < 3; i++) {
        const nodeX = startX;
        const nodeY = centerVertical - nodeH / 2 + (i - 1) * 60;
        const node: MindMapNode = {
          children: [],
          text: dir === 1 ? 'Left Node' : 'Right Node',
          xywh: `[${nodeX},${nodeY},${nodeW},${nodeH}]`,
        };
        if (depth > 1) {
          for (let j = 0; j < 2; j++) {
            const subX = dir === 1 ? nodeX + 150 + nodeW : nodeX - 150 - nodeW;
            const subY = nodeY - 30 + j * 60;
            node.children.push({
              children: [],
              text: 'Sub Node',
              xywh: `[${subX},${subY},${nodeW},${nodeH}]`,
            });
          }
        }
        root.children.push(node);
      }
    }

    const crud = edgeless.std.get(EdgelessCRUDIdentifier);
    const mindmapId = crud.addElement('mindmap', {
      style: mindmapStyle,
      children: root,
      layoutType: layoutType,
    }) as string;

    edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
      control: 'toolbar:dnd',
      page: 'whiteboard editor',
      module: 'toolbar',
      segment: 'toolbar',
      type: 'mindmap',
    });

    return mindmapId;
  };

export const textRender: DraggableTool['render'] = async (bound, edgeless) => {
  const vCenter = bound.y + bound.h / 2;
  const w = 100;
  const h = 32;

  const flag = edgeless.store
    .get(FeatureFlagService)
    .getFlag('enable_edgeless_text');
  let id: string;
  if (flag) {
    const [_, { textId }] = edgeless.std.command.exec(
      insertEdgelessTextCommand,
      {
        x: bound.x,
        y: vCenter - h / 2,
      }
    );
    id = textId!;
  } else {
    const crud = edgeless.std.get(EdgelessCRUDIdentifier);
    id = crud.addElement(CanvasElementType.TEXT, {
      xywh: new Bound(bound.x, vCenter - h / 2, w, h).serialize(),
      text: new Y.Text(),
    }) as string;

    edgeless.store.captureSync();
    const textElement = crud.getElementById(id);
    if (!(textElement instanceof TextElementModel)) {
      console.error('Cannot mount text editor on a non-text element');
      return null;
    }
    mountTextElementEditor(textElement, edgeless);
  }

  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'toolbar:dnd',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'text',
  });

  return id;
};

export const mediaRender: DraggableTool['render'] = async (bound, edgeless) => {
  let file: File | null = null;
  try {
    file = await openSingleFileWith();
  } catch (e) {
    console.error(e);
    return null;
  }
  if (!file) return null;

  const files = [file];
  const std = edgeless.std;
  const point: IVec = [bound.x, bound.y];

  // image
  if (file.type.startsWith('image/')) {
    const [id] = await addImages(std, files, {
      point,
      maxWidth: MAX_IMAGE_WIDTH,
      shouldTransformPoint: false,
    });
    return id;
  }

  // attachment
  const [id] = await addAttachments(std, files, point, false);
  return id;
};

const toolStyle2StyleObj = (state: ConfigState, style: ConfigStyle = {}) => {
  const styleObj = {} as Record<string, string>;
  for (const [key, value] of Object.entries(style)) {
    styleObj[`--${state}-${key}`] = `${value}${unitMap[key as ConfigProperty]}`;
  }
  return styleObj;
};
export const toolConfig2StyleObj = (config: ToolConfig) => {
  const styleObj = {} as Record<string, string>;
  for (const [state, style] of Object.entries(config)) {
    Object.assign(
      styleObj,
      toolStyle2StyleObj(state as ConfigState, {
        ...config.default,
        ...style,
      })
    );
  }
  return styleObj;
};
